import React, { useState } from "react";
import {
  Button, HStack, Text,
  Box, Flex, VStack, Select,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb,
} from "@chakra-ui/react";

export const LoanCalculator2 = ({ ...props }) => {
  const [amount, setAmount] = useState(500);
  const [paymentFrequency, setPaymentFrequency] = useState("Biweekly");
  const [loanTermMonths, setLoanTermMonths] = useState("12");

  const interestRate = 0.3099; // 30.99%

  const parsedAmount = Number(amount) || 100;
  const termInMonths = Number(loanTermMonths) || 12;
  const periods = paymentFrequency === "Biweekly"
    ? Math.max(1, Math.round((termInMonths / 12) * 26))
    : Math.max(1, termInMonths);
  const totalWithInterest = parsedAmount * (1 + interestRate * (termInMonths / 12));
  const periodicPayment = totalWithInterest / periods;

  const normalizeAmount = (value) => {
    if (Number.isNaN(value)) {
      return 500;
    }
    const rounded = Math.round(value / 100) * 100;
    return Math.min(20000, Math.max(500, rounded));
  };

  const inputBg = "white";
  const textColor = "gray.600";
  const textColorDark = "gray.700";
  const formatMoney = (value) => Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Box
      {...props}
      borderRadius="3xl"
      border="2px solid"
      borderColor="gray.200"
      boxShadow="0 22px 60px rgba(15, 14, 14, 0.2)"
      p={3}
      maxW="480px"
      w="100%"
      mb={{ base: -8, md: 2, lg: 2 }}
    >
      <VStack align="stretch" spacing={{ base: 0, md: 2, lg: 2 }}>
        <Text fontSize={16} fontWeight="bold" color={textColorDark} textAlign="center" py={2}>
          See repayment estimate
        </Text>

        {/* You Borrow */}
        <Box>
          <Flex
            bg={inputBg}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="2xl"
            p={3}
            boxShadow="sm"
            align="center"
            gap={2}
            position="relative"
            flexDirection="column"
            alignItems="flex-start"
          >
            <Text fontSize={12} fontWeight="600" color="gray.500" position="absolute" top="2" left="4" textTransform="uppercase" letterSpacing="0.4px">
              You borrow
            </Text>
            <Flex w="100%" align="center" gap={3} mt="5">
              <Text
                color="gray.700"
                fontSize={{ base: "xl", sm: "2xl" }}
                fontWeight="bold"
                px={3}
                py={2}
                bg="gray.50"
                borderRadius="md"
                w="100%"
              >
                ${formatMoney(parsedAmount)}
              </Text>
            </Flex>
          </Flex>
        </Box>

        <Box px={2} py={1}>
          <Flex align="center" gap={3}>
            <Text
              fontSize={12}
              color={textColor}
              fontWeight="600"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              px={2}
              py={1}
              borderRadius="md"
              minW="56px"
              textAlign="center"
            >
              $500
            </Text>
            <Slider
              min={500}
              max={20000}
              step={100}
              value={parsedAmount}
              onChange={(value) => setAmount(normalizeAmount(value))}
              focusThumbOnChange={false}
              flex={1}
            >
              <SliderTrack bg="gray.200" h="10px" borderRadius="full">
                <SliderFilledTrack bg="#111111" borderRadius="full" />
              </SliderTrack>
              <SliderThumb
                boxSize={6}
                bg="white"
                border="2px solid"
                borderColor="#111111"
                boxShadow="0 2px 8px rgba(0,0,0,0.2)"
                _hover={{ transform: 'scale(1.05)' }}
                _active={{ transform: 'scale(1.1)' }}
              />
            </Slider>
            <Text
              fontSize={12}
              color={textColor}
              fontWeight="600"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              px={2}
              py={1}
              borderRadius="md"
              minW="68px"
              textAlign="center"
            >
              $20,000
            </Text>
          </Flex>
        </Box>

        {/* You Pay */}
        <Box>
          <Flex
            bg={inputBg}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="2xl"
            p={3}
            boxShadow="sm"
            align="center"
            gap={2}
            position="relative"
            flexDirection="column"
            alignItems="flex-start"
          >
            <Text fontSize={12} fontWeight="600" color="gray.500" position="absolute" top="2" left="4" textTransform="uppercase" letterSpacing="0.4px">
              You pay
            </Text>
            <Flex w="100%" align="center" gap={1} mt="5"  ml={{ base: 2, md: 0 }}>
              <Box
                flex={1.2}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                pl={1}
                py={2}
                ml={{ base: -4, md: 0 }}
                bg="gray.50"
              >
                <Text fontSize={{ base: "md", sm: "lg" }} fontWeight="700" color="gray.700">
                  ${formatMoney(periodicPayment)}
                </Text>
              </Box>
              <Select
                flex={{ base: 1.3, sm: 1.1 }}
                minW={{ base: "100px", sm: "120px" }}
                maxW={{ base: "120px", sm: "140px" }}
                value={paymentFrequency}
                onChange={(e) => setPaymentFrequency(e.target.value)}
                borderColor="gray.200"
                borderRadius="md"
                bg="white"
                fontSize={{ base: "xs", sm: "sm" }}
                fontWeight="600"
                h="38px"
                _focus={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}
                _active={{ borderColor: "black"}}
              >
                <option value="Biweekly">Biweekly</option>
                <option value="Monthly">Monthly</option>
              </Select>
              <Select
                flex={{ base: 1.6, sm: 1.3 }}
                minW={{ base: "110px", sm: "120px" }}
                maxW={{ base: "140px", sm: "170px" }}
                 pl={0}
                value={loanTermMonths}
                onChange={(e) => setLoanTermMonths(e.target.value)}
                borderColor="gray.200"
                borderRadius="md"
                bg="white"
                fontSize={{ base: "xs", sm: "sm" }}
                fontWeight="600"
                h="38px"
                _focus={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}
                _active={{ borderColor: "black" }}
              >
                <option value="12">12 months</option>
                <option value="24">24 months</option>
                <option value="36">36 months</option>
                <option value="48">48 months</option>
              </Select>
            </Flex>
          </Flex>
        </Box>

        {/* Loan details */}
        <Flex fontSize={14} color={textColor} py={3} justify="space-between" align="center" w="100%">
          <Text>APR: {(interestRate * 100).toFixed(2)}%</Text>
          <Text>Processing fee: 0.00</Text>
        </Flex>

        <Button
          as="a"
          href="#download-section"
          bg="rgb(20, 20, 20)"
          color="white"
          fontWeight="bold"
          size="lg"
          borderRadius="full"
          px={10}
          _hover={{ bg: "rgb(10, 10, 10)", transform: "scale(1.05)" }}
          transition="all 0.2s"
        >
          Apply now
        </Button>
      </VStack>
    </Box>
  );
};

export default LoanCalculator2;
